import { createSystem, defaultConfig, defineConfig } from "@chakra-ui/react";

const config = defineConfig({
  theme: {
    tokens: {
      colors: {
        white: { value: "#FFFFFF" },
        brand: {
          primary: { value: "#5667D3" },
          50:  { value: "#EEEEF8" },
          100: { value: "#E0E2F5" },
          200: { value: "#C5C9EE" },
          300: { value: "#9FA7E4" },
          400: { value: "#7A84D9" },
          500: { value: "#5667D3" },
          600: { value: "#5667D3" },
          700: { value: "#3A4FAD" },
          800: { value: "#2E3F8C" },
          900: { value: "#1E2B66" },
          950: { value: "#131A3F" },
        },
        warm: {
          50:  { value: "#fafaf7" },
          100: { value: "#f3f2ed" },
          200: { value: "#ecebe5" },
          300: { value: "#d8d7d0" },
          400: { value: "#b5b4ad" },
          500: { value: "#9a958a" },
          600: { value: "#6b665b" },
          700: { value: "#45413a" },
          800: { value: "#1a1815" },
          900: { value: "#0d0c0a" },
        },
        slate: {
          50:  { value: "#F8FAFC" },
          100: { value: "#F1F5F9" },
          200: { value: "#E2E8F0" },
          300: { value: "#CBD5E1" },
          400: { value: "#94A3B8" },
          500: { value: "#64748B" },
          600: { value: "#475569" },
          700: { value: "#334155" },
          800: { value: "#1E293B" },
          900: { value: "#0F172A" },
          950: { value: "#020617" },
        },
        dark: {
          primary:   { value: "#0f0e0c" },
          secondary: { value: "#131210" },
          card:      { value: "#181715" },
          muted:     { value: "#1f1d1a" },
          border:    { value: "rgba(255,250,240,0.07)" },
        },
        success: {
          50:  { value: "#F0FDF4" },
          100: { value: "#DCFCE7" },
          500: { value: "#22C55E" },
          600: { value: "#16A34A" },
          700: { value: "#15803D" },
          800: { value: "#166534" },
        },
        error: {
          50:  { value: "#FEF2F2" },
          100: { value: "#FEE2E2" },
          500: { value: "#EF4444" },
          600: { value: "#DC2626" },
          700: { value: "#B91C1C" },
          800: { value: "#991B1B" },
        },
        warning: {
          500: { value: "#F59E0B" },
          600: { value: "#D97706" },
          700: { value: "#B45309" },
          800: { value: "#92400E" },
        }
      },
      fonts: {
        heading: { value: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
        body:    { value: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
        mono:    { value: "'JetBrains Mono', ui-monospace, 'Cascadia Code', 'Source Code Pro', monospace" },
      },
    },
    semanticTokens: {
      colors: {
        text: {
          main:        { value: { _light: "#1a1815",  _dark: "#f5f2ec"  } },
          secondary:   { value: { _light: "#45413a",  _dark: "#c9c4b9"  } },
          muted:       { value: { _light: "#6b665b",  _dark: "#908a7e"  } },
          inverted:    { value: { _light: "white",    _dark: "#1a1815"           } },
          onDark:      { value: "white" },
          onDarkMuted: { value: "rgba(255, 255, 255, 0.85)" },
        },
        landing: {
          bg:      { value: { _light: "white",             _dark: "{colors.dark.primary}"   } },
          surface: { value: { _light: "{colors.warm.100}", _dark: "{colors.dark.secondary}" } },
          card:    { value: { _light: "white",             _dark: "{colors.dark.card}"      } },
          border:  { value: { _light: "{colors.warm.200}", _dark: "{colors.dark.border}"    } },
        },
        app: {
          bg:      { value: { _light: "#fafaf7",                       _dark: "{colors.dark.primary}" } },
          surface: { value: { _light: "#ffffff",                       _dark: "{colors.dark.card}"    } },
          border:  { value: { _light: "rgba(20, 18, 14, 0.08)",        _dark: "{colors.dark.border}"  } },
          muted:   { value: { _light: "#f3f2ed",                       _dark: "{colors.dark.muted}"   } },
          sidebar: {
            bg:     { value: { _light: "#fafaf7",                      _dark: "{colors.dark.primary}" } },
            border: { value: { _light: "rgba(20, 18, 14, 0.08)",       _dark: "{colors.dark.border}"  } },
            hover:  { value: { _light: "#f3f2ed",                      _dark: "{colors.dark.muted}"   } },
            active: { value: { _light: "#f3f2ed",                      _dark: "rgba(86,103,211,0.15)" } },
          }
        },
        brand: {
          solid:         { value: "{colors.brand.primary}"                                              },
          "solid/hover": { value: "{colors.brand.400}"                                                  },
          muted:         { value: { _light: "{colors.brand.100}", _dark: "{colors.brand.900}" }         },
          subtle:        { value: { _light: "{colors.brand.50}",  _dark: "{colors.brand.950}" }         },
        },
        cta: {
          solid:         { value: "{colors.brand.primary}" },
          "solid/hover": { value: "{colors.brand.400}"     },
          glow:          { value: "rgba(86, 103, 211, 0.25)" },
          on:            { value: "white" },
        },
      },
    },
  },
  globalCss: {
    "html, body": {
      bg: "landing.bg",
      color: "text.main",
    },
    "h1,h2,h3,h4,h5,h6": {
      fontWeight: "800",
      letterSpacing: "-0.02em",
    },
  },
});

export const system = createSystem(defaultConfig, config);
